import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './apollo';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AccountProvider } from './context/AccountContext';
import { ProductProvider, useProduct, PRODUCT_HOME } from './context/ProductContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProductRoute } from './components/common/ProductRoute';
import { GaPageViews } from './components/common/GaPageViews';
import { SidebarProvider } from './context/SidebarContext';
import { ProfileSelectionProvider } from './context/ProfileSelectionContext';
import { Login } from './components/account/Login';
import { SignUp } from './components/account/SignUp';
import PaymentPage from './components/account/PaymentPage';
import OrderConfirmationPage from './components/account/OrderConfirmationPage';
import ConnectWordPress from './components/account/ConnectWordPress';
import { Dashboard } from './components/layouts/Dashboard';
import { AutomationRulesLayout } from './components/layouts/AutomationRules';
import { SettingsLayout } from './components/layouts/Settings';
import { Profile } from './components/settings/Profile';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import TwitterCallback from './components/auth/TwitterCallback';
import FacebookCallback from './components/auth/FacebookCallback';
import ThreadsCallback from './components/auth/ThreadsCallback';
import InstagramCallback from './components/auth/InstagramCallback';
import YouTubeCallback from './components/auth/YouTubeCallback';
import TikTokCallback from './components/auth/TikTokCallback';
import SnapchatCallback from './components/auth/SnapchatCallback';
import { ProjectsLayout } from './components/layouts/Projects';
import { ResearchLayout } from './components/layouts/Research';
import { AssemblerLayout } from './components/layouts/Assembler';
import { ContentLayout } from './components/layouts/Content';
import { ContentSettings } from './components/layouts/ContentSettings';
import { ContentDetail } from './components/layouts/ContentDetail';
import { MediaLibrary } from './components/layouts/MediaLibrary';
import { CollectionLayout } from './components/layouts/Collection';
import { CaptureLayout } from './components/layouts/Capture';
import { CollectionItemDetail } from './components/layouts/CollectionItemDetail';
import { AppShell } from './components/nav/AppShell';
import { CmsMediaSettings } from './components/cms/CmsMediaSettings';
import { CmsSite } from './components/cms/CmsSite';
import { CmsHistory } from './components/cms/CmsHistory';
import { CmsToday } from './components/cms/CmsToday';
import { CmsSites } from './components/cms/CmsSites';
import { AtomizeSection, ReachSection } from './components/cms/Sections';
import { AddonsRedirect, SiteRedirect } from './components/cms/redirects';
import { ReachCalendar } from './components/reach/ReachCalendar';
import { ReachCampaigns } from './components/reach/ReachCampaigns';
import { ReachChannels } from './components/reach/ReachChannels';

/** Old TinyCMS article URLs open the one article page. */
const CmsArticleRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/content/${id}` : '/content'} replace />;
};

/** Signed-in routes share the Ink top bar. Login and callbacks stay outside it.
 *  A blocked visit hands its destination to login via router state, not
 *  sessionStorage, so the notice stays on this navigation. */
const AuthedShell: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-cmp-ink">Loading...</div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <AppShell />;
};

// Collect's media library. Atomizer's library lives inside Content.
const MediaRoute: React.FC = () => {
  const { product, isLoading } = useProduct();
  if (isLoading) return null;
  if (product === 'atomizer') return <Navigate to="/content?view=media" replace />;
  return <MediaLibrary />;
};

// The app's landing route belongs to the active shell: Reach's Today
// dashboard, or the Content Engine library.
const HomeRoute: React.FC = () => {
  const { product, isLoading } = useProduct();
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-white">Loading...</div>
    );
  }
  // Every shell but PostMaster lands on its own home (TinyCMS → /today,
  // Collect → /collection).
  if (product !== 'reach') {
    return <Navigate to={PRODUCT_HOME[product]} replace />;
  }
  return (
    <ProductRoute product="reach">
      <Dashboard />
    </ProductRoute>
  );
};

// Routing outside /account is owned by the edge (Cloudflare in production,
// scripts/dev-edge.js locally), which serves the marketing sites. The app never
// handles those paths.
function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <ThemeProvider>
          <AccountProvider>
            <ProductProvider>
              <SidebarProvider>
                <ProfileSelectionProvider>
                  <ErrorBoundary>
                    <BrowserRouter basename="/account">
                      <GaPageViews />
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignUp />} />
                        {/* WordPress plugin connect landing (handles its own auth to
                        preserve the pairing query params). */}
                        <Route path="/connect" element={<ConnectWordPress />} />
                        <Route path="/payment" element={<PaymentPage />} />
                        <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                        <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
                        <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
                        <Route path="/auth/threads/callback" element={<ThreadsCallback />} />
                        <Route path="/auth/instagram/callback" element={<InstagramCallback />} />
                        <Route path="/auth/youtube/callback" element={<YouTubeCallback />} />
                        <Route path="/auth/tiktok/callback" element={<TikTokCallback />} />
                        <Route path="/auth/snapchat/callback" element={<SnapchatCallback />} />
                        <Route element={<AuthedShell />}>
                          {/* Landing route: Reach's Today, or the Engine library. */}
                          <Route path="/" element={<HomeRoute />} />
                          {/* ── Reach shell ─────────────────────────────────── */}
                          <Route
                            path="/calendar"
                            element={
                              <ReachSection>
                                <ReachCalendar />
                              </ReachSection>
                            }
                          />
                          <Route
                            path="/projects"
                            element={
                              <ReachSection>
                                <ReachCampaigns />
                              </ReachSection>
                            }
                          />
                          <Route
                            path="/projects/manage"
                            element={
                              <ReachSection>
                                <ProjectsLayout />
                              </ReachSection>
                            }
                          />
                          <Route
                            path="/automation-rules"
                            element={
                              <ReachSection>
                                <AutomationRulesLayout />
                              </ReachSection>
                            }
                          />
                          {/* ── Collect shell ───────────────────────────────── */}
                          <Route
                            path="/collection"
                            element={
                              <ProductRoute product="collect">
                                <CollectionLayout />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/collection/:id"
                            element={
                              <ProductRoute product="collect">
                                <CollectionItemDetail />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/capture"
                            element={
                              <ProductRoute product="collect">
                                <CaptureLayout />
                              </ProductRoute>
                            }
                          />
                          {/* TinyCMS shell. The atomizer id is that shell; the old
                            /cms paths redirect onto it. */}
                          <Route
                            path="/today"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsToday />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/sites"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsSites />
                              </ProductRoute>
                            }
                          />
                          {/* Reach (Calendar · Campaigns · Channels) and Atomize
                            replace Add-ons and the old Amplify dashboard. */}
                          <Route
                            path="/channels"
                            element={
                              <ReachSection>
                                <ReachChannels />
                              </ReachSection>
                            }
                          />
                          <Route path="/reach" element={<Navigate to="/calendar" replace />} />
                          <Route path="/amplify" element={<Navigate to="/calendar" replace />} />
                          <Route path="/atomize" element={<Navigate to="/research" replace />} />
                          <Route path="/addons" element={<AddonsRedirect />} />
                          <Route path="/cms" element={<Navigate to="/today" replace />} />
                          <Route
                            path="/cms/articles"
                            element={<Navigate to="/content" replace />}
                          />
                          <Route path="/cms/articles/:id" element={<CmsArticleRedirect />} />
                          <Route
                            path="/cms/content-settings"
                            element={<Navigate to="/content/settings" replace />}
                          />
                          <Route path="/cms/site" element={<Navigate to="/site" replace />} />
                          <Route path="/cms/addons" element={<Navigate to="/addons" replace />} />
                          <Route
                            path="/cms/history"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsHistory />
                              </ProductRoute>
                            }
                          />
                          {/* Collect keeps Media as its own place. Atomizer folds it
                            into Content, as Articles | Media. */}
                          <Route path="/media" element={<MediaRoute />} />
                          {/* ── Content Engine shell ────────────────────────── */}
                          <Route
                            path="/research"
                            element={
                              <ProductRoute product="atomizer">
                                <AtomizeSection>
                                  <ResearchLayout />
                                </AtomizeSection>
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/assembler"
                            element={
                              <ProductRoute product="atomizer">
                                <AtomizeSection>
                                  <AssemblerLayout />
                                </AtomizeSection>
                              </ProductRoute>
                            }
                          />
                          {/* The site's look is Content → Design; the rest is Site settings. */}
                          <Route path="/site" element={<SiteRedirect />} />
                          <Route
                            path="/content/media/settings"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsMediaSettings />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/site/settings"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsSite mode="settings" />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/content/settings"
                            element={
                              <ProductRoute product="atomizer">
                                <ContentSettings />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/content"
                            element={
                              <ProductRoute product="atomizer">
                                <ContentLayout />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/content/:id"
                            element={
                              <ProductRoute product="atomizer">
                                <ContentDetail />
                              </ProductRoute>
                            }
                          />
                          <Route path="/settings" element={<SettingsLayout />}>
                            <Route path="profile" element={<Profile />} />
                          </Route>
                        </Route>
                        {/* Add other protected routes here */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </BrowserRouter>
                  </ErrorBoundary>
                </ProfileSelectionProvider>
              </SidebarProvider>
            </ProductProvider>
          </AccountProvider>
        </ThemeProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;
